"use client";

import React, { useMemo } from "react";
import { Navigate, useSearchParams, useLocation } from "react-router-dom";
import { useBrand, DashboardConfig } from "@webuild-ai/react-auth";

import { IframeDashboard, MicrofrontendDashboard } from "./dashboard";

/**
 * DashboardPage - Flexible dashboard embedding with multiple strategies
 *
 * Supports three embedding types configured per brand:
 * 1. iframe - External vendors (Looker, PowerBI) or different tech stacks
 * 2. microfrontend - Internal React apps via Module Federation (future)
 * 3. component - Local React components (future)
 *
 * URL Priority for iframe type:
 * 1. Query param: ?dashboardUrl=https://example.com (for demos)
 * 2. brand.dashboard.url (primary config format)
 * 3. VITE_DASHBOARD_URL environment variable (fallback)
 *
 * URL routing:
 * - Query param: ?tab=partners enables bookmarkable dashboard tabs (list pages)
 * - URL segments: /ncrm/partners/1 enables bookmarkable detail pages
 * - Supported tabs: overview, partners, contacts, meetings, government, regulatory, performance
 *
 * If no URL/config is available, redirects to home page.
 */
export const DashboardPage: React.FC = () => {
  const { brand } = useBrand();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Support URL override via query param for demos
  const urlOverride = searchParams.get("dashboardUrl") || undefined;

  // Extract tab from URL for bookmarkable dashboard navigation
  const initialTab = searchParams.get("tab") || undefined;

  // Extract sub-path from URL for detail page deep-linking
  // e.g., /ncrm/partners/1 → /partners/1
  const urlSegment = brand.dashboard?.urlSegment ?? "dashboard";
  const prefix = `/${urlSegment}`;
  const initialPath =
    location.pathname.length > prefix.length && location.pathname.startsWith(prefix)
      ? location.pathname.slice(prefix.length)
      : undefined;

  // Build dashboard config
  const dashboardConfig = useMemo((): DashboardConfig | null => {
    // Use brand dashboard config
    if (brand.dashboard) {
      return brand.dashboard;
    }

    // Fallback to environment variable
    if (import.meta.env.VITE_DASHBOARD_URL) {
      return {
        type: "iframe",
        url: import.meta.env.VITE_DASHBOARD_URL,
      };
    }

    return null;
  }, [brand.dashboard]);

  // Redirect to home if no dashboard configured and no URL override
  if (!dashboardConfig && !urlOverride) {
    return <Navigate replace to="/" />;
  }

  // If we have a URL override but no config, create an iframe config
  const effectiveConfig: DashboardConfig = dashboardConfig || {
    type: "iframe",
    url: urlOverride,
  };

  // Render based on dashboard type
  switch (effectiveConfig.type) {
    case "iframe":
      return (
        <IframeDashboard
          brand={brand}
          config={effectiveConfig}
          initialPath={initialPath}
          initialTab={initialTab}
          urlOverride={urlOverride}
        />
      );

    case "microfrontend":
      return <MicrofrontendDashboard brand={brand} config={effectiveConfig} />;

    case "component":
      // Future: Local component rendering
      // return <ComponentDashboard brand={brand} config={effectiveConfig} />;
      return <Navigate replace to="/" />;

    default:
      return <Navigate replace to="/" />;
  }
};
